import React, { useEffect, useState, useRef } from "react";
import { Box, CircularProgress, Typography, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { colors } from "../../../theme/colors";
import { sessionsService } from "../../../services/sessionsService";
import {
  pauseInactivityTimer,
  resumeInactivityTimer,
} from "../../../hooks/useInactivityTimer";
import { usePreventNavigation } from "../../../hooks";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

interface TranscriptLoadingModalProps {
  sessionId: number;
  onComplete: () => void;
  onCancel?: () => void;
}

interface ProcessingStage {
  labelKey: string;
  completed: boolean;
}

const POLLING_INTERVALS = [3000, 5000, 5000, 10000, 10000, 15000, 20000, 30000];

const MAX_POLLING_TIME = 6 * 60 * 1000;
const ESTIMATED_DURATION = 4.5 * 60 * 1000;

const TranscriptLoadingModal: React.FC<TranscriptLoadingModalProps> = ({
  sessionId,
  onComplete,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [checkCount, setCheckCount] = useState(0);
  const [timeoutReached, setTimeoutReached] = useState(false);
  const [stages, setStages] = useState<ProcessingStage[]>([
    { labelKey: "transcriptLoading.stages.audioUploaded", completed: true },
    { labelKey: "transcriptLoading.stages.transcribing", completed: false },
    { labelKey: "transcriptLoading.stages.formatting", completed: false },
  ]);

  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(Date.now());
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Prevent browser back button during processing
  usePreventNavigation(!timeoutReached);

  useEffect(() => {
    pauseInactivityTimer();
    return () => {
      resumeInactivityTimer();
    };
  }, []);

  const getCurrentInterval = (count: number): number => {
    const index = Math.min(count, POLLING_INTERVALS.length - 1);
    return POLLING_INTERVALS[index];
  };

  useEffect(() => {
    let isMounted = true;

    progressIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const progress = Math.min((elapsed / ESTIMATED_DURATION) * 90, 90);

      if (progress > 30 && !stages[1].completed) {
        setStages((prev) => [
          prev[0],
          { ...prev[1], completed: true },
          prev[2],
        ]);
      }
    }, 1000);

    const checkTranscript = async () => {
      try {
        const elapsed = Date.now() - startTimeRef.current;

        if (elapsed > MAX_POLLING_TIME) {
          setTimeoutReached(true);
          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
          }
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }
          return;
        }

        const session = await sessionsService.getSessionById(sessionId);

        if (!isMounted) return;

        if (session.transcript && session.transcript.trim().length > 0) {
          setStages((prev) =>
            prev.map((stage) => ({ ...stage, completed: true })),
          );

          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
          }
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }

          setTimeout(() => {
            if (isMounted) {
              onComplete();
            }
          }, 500);
        } else {
          const newCount = checkCount + 1;
          setCheckCount(newCount);

          const nextInterval = getCurrentInterval(newCount);
          pollingTimeoutRef.current = setTimeout(checkTranscript, nextInterval);
        }
      } catch (error) {
        console.error("[TranscriptLoading] Error checking transcript:", error);

        if (!isMounted) return;

        const newCount = checkCount + 1;
        setCheckCount(newCount);
        const nextInterval = getCurrentInterval(newCount);
        pollingTimeoutRef.current = setTimeout(checkTranscript, nextInterval);
      }
    };

    pollingTimeoutRef.current = setTimeout(checkTranscript, 3000);

    return () => {
      isMounted = false;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, checkCount, onComplete]);

  if (timeoutReached) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "40px",
          gap: 3,
        }}
      >
        <AccessTimeIcon sx={{ fontSize: 60, color: colors.amber.main }} />
        <Typography
          variant="h6"
          sx={{
            color: colors.text.primary,
            fontWeight: 600,
            textAlign: "center",
            fontFamily: "Nunito",
          }}
        >
          {t("transcriptLoading.timeoutTitle")}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: colors.text.secondary,
            textAlign: "center",
            fontFamily: "Nunito",
            maxWidth: "400px",
          }}
        >
          {t("transcriptLoading.timeoutSubtitle")}
        </Typography>
        <Box sx={{ display: "flex", gap: 2, marginTop: 2 }}>
          {onCancel && (
            <Button variant="outlined" onClick={onCancel}>
              {t("transcriptLoading.cancel")}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={() => {
              setTimeoutReached(false);
              setCheckCount(0);
              startTimeRef.current = Date.now();
              setStages([
                {
                  labelKey: "transcriptLoading.stages.audioUploaded",
                  completed: true,
                },
                {
                  labelKey: "transcriptLoading.stages.transcribing",
                  completed: false,
                },
                {
                  labelKey: "transcriptLoading.stages.formatting",
                  completed: false,
                },
              ]);
            }}
          >
            {t("transcriptLoading.continueWaiting")}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "40px",
        gap: 3,
        minWidth: "450px",
      }}
    >
      <CircularProgress
        size={60}
        sx={{
          color: colors.primary.main,
        }}
      />

      <Typography
        variant="h6"
        sx={{
          color: colors.text.primary,
          fontWeight: 600,
          textAlign: "center",
          fontFamily: "Nunito",
        }}
      >
        {t("transcriptLoading.title")}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          color: colors.text.secondary,
          textAlign: "center",
          fontFamily: "Nunito",
          maxWidth: "400px",
        }}
      >
        {t("transcriptLoading.subtitle")}
      </Typography>

      <Box sx={{ width: "100%", maxWidth: "400px" }}>
        {stages.map((stage, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              marginBottom: 1,
            }}
          >
            {stage.completed ? (
              <CheckCircleIcon
                sx={{ fontSize: 20, color: colors.status.success }}
              />
            ) : (
              <CircularProgress size={16} thickness={6} />
            )}
            <Typography
              variant="body2"
              sx={{
                color: stage.completed
                  ? colors.text.primary
                  : colors.text.secondary,
                fontFamily: "Nunito",
                fontWeight: stage.completed ? 500 : 400,
              }}
            >
              {t(stage.labelKey)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default TranscriptLoadingModal;
